import { isAxiosError } from 'axios';

export type TemplateSaveErrorReason =
  | 'compliance_block'
  | 'conflict'
  | 'network'
  | 'timeout'
  | 'unknown';

const TIMEOUT_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT', 'ERR_TIMEOUT']);
const TIMEOUT_STATUSES = new Set([408, 504]);

/**
 * Reduces a failed template save to a non-PHI reason code.
 *
 * The save payload is clinician-authored, so the error itself can never reach
 * Datadog (see `sanitizeError`). This is what remains queryable: enough to tell
 * a compliance block from a transport failure without exporting any content.
 */
export function classifyTemplateSaveError(error: unknown): TemplateSaveErrorReason {
  if (!isAxiosError(error)) {
    return 'unknown';
  }

  const detailType = (error.response?.data as { detail?: { type?: string } } | undefined)
    ?.detail?.type;
  if (detailType === 'ComplianceCheckFailedError') {
    return 'compliance_block';
  }

  if (error.code && TIMEOUT_CODES.has(error.code)) {
    return 'timeout';
  }
  if (error.code === 'ERR_NETWORK' || !error.response) {
    return 'network';
  }
  if (TIMEOUT_STATUSES.has(error.response.status)) {
    return 'timeout';
  }
  if (error.response.status === 409) {
    return 'conflict';
  }

  return 'unknown';
}
